import {NgModule} from '@angular/core';
import {LocationStrategy, PathLocationStrategy} from '@angular/common';
import {PreloadAllModules, RouterModule, Routes} from "@angular/router";
import {Constants} from "./modules/constants";
import {ReferralCreditComponent} from "./modules/referral/referral-credit.component";
import {LogoutComponent} from "./modules/auth/logout.component";
import {ProductComponent} from "./modules/product/product.component";
import {ProductRouteGuard} from "./modules/product/product-route.guard";
import {PackComponent} from "./modules/pack/pack.component";
import {ReloadComponent} from "./modules/common/reload.component";
import {ConfirmExitGuard} from "./modules/route-guards/confirm-exit.guard";
import {PayPalTokenComponent} from "./modules/payment/paypal/paypal-token.component";
import {UserRolesGuard} from "./modules/route-guards/user-roles.guard";
import {CartOverviewComponent} from "./modules/cart/cart-overview/cart-overview.component";
import {SurveyResultsComponent} from "./modules/survey/survey-results.component";
import {PaymentCallbackComponent} from "./modules/payment/payment-callback.component";
import {CATALOG_ROUTES} from "./modules/cart/catalog/catalog.routes";
import {SHAREABLE_ROUTES} from "./modules/social/shareable/shareable.routes";
import {ORDER_HISTORY_ROUTES} from "./modules/orders/order-history.routes";
import {SKIN_ANALYZER_ROUTES} from "./modules/skin-analyzer/results/skin-analyzer.routes";

export const cartUxLocaleRoutes: Routes = [
    {
        path: Constants.AUTO_ORDER_STATE,
        loadChildren: () => import('./modules/autoorder/autoorder.module')
            .then(module => module.AutoorderModule)
    },
    {
        path: Constants.MHP_STATE,
        loadChildren: () => import('./modules/mhp/mhp.module')
            .then(module => module.MhpModule)
    },
    {
        path: Constants.CHECKOUT_STATE,
        loadChildren: () => import('./checkout/checkout.module')
            .then(module => module.CheckoutModule)
    },
    {
        path: Constants.CBEC_STATE,
        loadChildren: () => import('./cbec/cbec.module')
            .then(module => module.CbecModule)
    },
    {
        path: Constants.AFFILIATE_STATE,
        loadChildren: () => import('./modules/affiliate/affiliate.module')
            .then(module => module.AffiliateModule)
    },
    {
        path: Constants.ENROLL_STATE,
        loadChildren: () => import('./modules/enroll/enroll.module')
            .then(module => module.EnrollModule)
    },
    {
        path: Constants.LANDING_STATE,
        loadChildren: () => import('./modules/landing/landing.module')
            .then(module => module.LandingModule)
    },
    {
        //queryParam option: {shareListId: number}
        path: Constants.SHARE_LIST_STATE,
        loadComponent: () => import("./modules/social/share-list.component").then(m => m.ShareListComponent),
        data: {
            roles: [Constants.ROLES.ASSOCIATE, Constants.ROLES.PC, Constants.ROLES.AFFILIATE],
            restrict: [Constants.ROLES.NFR_MHP_ONLY, Constants.ROLES.EVENT]
        }
    },
    {
        path: Constants.CART_OVERVIEW_STATE,
        component: CartOverviewComponent,
    },
    {
        path: Constants.REFERRAL_CREDIT_STATE,
        component: ReferralCreditComponent,
        data: {
            roles: [Constants.ROLES.PC],
            restrict: [Constants.ROLES.EVENT]
        }
    },
    {
        path: Constants.PAYPAL_TOKEN_STATE,
        component: PayPalTokenComponent,
        data: {
            roles: [Constants.ROLES.ANY],
        }
    },
    {
        path: Constants.PRODUCT_STATE + '/:usanaId',
        component: ProductComponent,
        canActivate: [ProductRouteGuard],
        runGuardsAndResolvers: 'always',
        data: {
            roles: [Constants.ROLES.ANY],
            restrict: [Constants.ROLES.NFR_MHP_ONLY]
        }
    },
    {
        path: Constants.PRODUCT_STATE + '/:usanaId/:readableUrl',
        component: ProductComponent,
        canActivate: [ProductRouteGuard],
        runGuardsAndResolvers: 'always',
        data: {
            roles: [Constants.ROLES.ANY],
            restrict: [Constants.ROLES.NFR_MHP_ONLY]
        }
    },
    {
        path: Constants.PACK_STATE + '/:packId',
        component: PackComponent,
        data: {
            roles: [Constants.ROLES.ASSOCIATE, Constants.ROLES.PC, Constants.ROLES.EMPLOYEE, Constants.ROLES.AFFILIATE],
            restrict: [Constants.ROLES.EVENT]
        }
    },
    {
        //queryParam option: {goToLogin: boolean}
        path: Constants.LOGOUT_STATE,
        component: LogoutComponent,
        data: {
            roles: [Constants.ROLES.ALWAYS]
        }
    },
    {
        //queryParam option: {url: string}
        path: Constants.RELOAD_STATE,
        component: ReloadComponent,
        data: {
            roles: [Constants.ROLES.ALWAYS]
        }
    },
    {
        //queryParam option: {surveyId: number}
        path: Constants.SURVEY_RESULTS_ROUTE,
        component: SurveyResultsComponent,
        data: {
            roles: [Constants.ROLES.ANY],
            restrict: [Constants.ROLES.NFR, Constants.ROLES.NFR_MHP_ONLY, Constants.ROLES.CBEC, Constants.ROLES.EVENT]
        }
    },
    {
        path: Constants.PAYMENT_CALLBACK_STATE + '/notify/:paymentMethodType/:paymentId',
        data: {
            mode: 'notify'
        },
        component: PaymentCallbackComponent
    },
    {
        path: Constants.ADMIN,
        loadChildren: () => import('./modules/admin/admin.module')
            .then(module => module.AdminModule)
    },
    {
        path: Constants.MARKETING_PAGE_STATE,
        loadChildren: () => import('./modules/marketing-pages/marketing-page-routing.module')
            .then(module => module.MarketingPageRoutingModule)
    },
    {
        path: Constants.MARKETING_PAGE_EMBED_STATE,
        loadChildren: () => import('./modules/marketing-pages/page-embed-routing.module')
            .then(module => module.PageEmbedRoutingModule)
    },
    // Feature route sets that previously self-mounted at ux/cart/:locale via eager
    // RouterModule.forChild. Spread here so they are ordinary cart routes: the :locale
    // parent supplies the guard, and CART_PATH_SEGMENTS derives their segments (DEV-97650).
    ...CATALOG_ROUTES,
    ...SHAREABLE_ROUTES,
    ...ORDER_HISTORY_ROUTES,
    ...SKIN_ANALYZER_ROUTES,
    {path: '', redirectTo: Constants.LANDING_STATE, pathMatch: 'full'},
    {path: '**', redirectTo: Constants.LANDING_STATE, pathMatch: 'full'},
];

// Top-level routes that live outside /ux/* and should bypass the dotcom/cart
// URL-prefix logic in AppComponent.modifyUrl. Add new root-level routes here.
export const rootAppRoutes: Routes = [
    {
        path: Constants.REFERRAL_REDIRECT_STATE + '/:code',
        loadChildren: () => import('./modules/referral-redirect/referral-redirect.module')
            .then(module => module.ReferralRedirectModule)
    }
];

export const appRoutes: Routes = [
    {
        path: 'ux',
        children: [
            {
                path: 'cart',
                children: [
                    {
                        path: ':locale',
                        canActivate: [UserRolesGuard],
                        canActivateChild: [UserRolesGuard],
                        children: cartUxLocaleRoutes,
                    },
                ],
            },
            {
                path: 'dotcom',
                loadChildren: () => import('./dotcom/dotcom.routes')
                    .then(m => m.DOTCOM_ROUTES),
            },
        ],
    },
    ...rootAppRoutes,
    // Fallback, this doesn't really get hit because app.component code intercepts it first
    {path: '**', redirectTo: `/${Constants.URL_BASE_DOTCOM}/${Constants.DOTCOM_HOME_STATE}`, pathMatch: 'full'},
];

@NgModule({
    imports: [
        RouterModule.forRoot(appRoutes, {
    // Must stay 'disabled': ScrollRestorationService, wired in AppComponent, owns scroll handling.
    scrollPositionRestoration: 'disabled',
    // enableTracing: true,
    preloadingStrategy: PreloadAllModules
})
    ],
    exports: [
        RouterModule
    ],
    providers: [
        ConfirmExitGuard,
        UserRolesGuard,
        {provide: LocationStrategy, useClass: PathLocationStrategy},
    ]
})
export class AppRoutingModule {
}
